import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Music2, Twitter, Youtube, Trash2, Loader2 } from "lucide-react";
import { authAxios } from "@/context/AuthContext";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram, prefix: "instagram.com/" },
  { key: "tiktok", label: "TikTok", icon: Music2, prefix: "tiktok.com/@" },
  { key: "twitter", label: "X / Twitter", icon: Twitter, prefix: "x.com/" },
  { key: "youtube", label: "YouTube", icon: Youtube, prefix: "youtube.com/@" },
];

export const DashboardSocials = () => {
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ platform: "instagram", handle: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAxios.get("/user/social-accounts")
      .then((r) => setSocials(r.data.social_accounts))
      .catch((err) => console.error("socials fetch failed:", err))
      .finally(() => setLoading(false));
  }, []);

  const add = async () => {
    if (!draft.handle.trim()) { toast.error("Enter your handle first"); return; }
    setSaving(true);
    try {
      const r = await authAxios.post("/user/social-accounts", { platform: draft.platform, handle: draft.handle.trim() });
      setSocials(r.data.social_accounts);
      setDraft({ ...draft, handle: "" });
      toast.success(`${draft.platform} linked`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (platform) => {
    try {
      const r = await authAxios.delete(`/user/social-accounts/${platform}`);
      setSocials(r.data.social_accounts);
      toast.success("Removed");
    } catch (err) {
      console.error("remove social failed:", err);
      toast.error("Failed to remove");
    }
  };

  return (
    <section data-testid="dashboard-socials-section" className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="font-heading font-semibold text-lg mb-1">My social accounts</h3>
      <p className="text-sm text-slate-500 mb-5">Add the handles where you&apos;ll post affiliate content. Members Club uses these to write better AI captions.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {loading ? (
          <div className="col-span-full text-slate-400 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : socials.length === 0 ? (
          <p className="col-span-full text-sm text-slate-400 italic">No socials linked yet.</p>
        ) : socials.map((s) => {
          const platform = PLATFORMS.find((p) => p.key === s.platform);
          const Icon = platform?.icon || Instagram;
          return (
            <div key={s.platform} data-testid={`social-row-${s.platform}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-slate-700" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{platform?.label}</p>
                  <p className="text-xs text-slate-500">@{s.handle}</p>
                </div>
              </div>
              <button data-testid={`remove-social-${s.platform}`} onClick={() => remove(s.platform)} className="p-1.5 text-slate-400 hover:text-rose-500" aria-label={`Remove ${s.platform}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <select
          data-testid="social-platform-select"
          value={draft.platform}
          onChange={(e) => setDraft({ ...draft, platform: e.target.value })}
          className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white"
        >
          {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <div className="flex-1 relative">
          <span className="absolute left-3 top-2.5 text-slate-400 text-sm">@</span>
          <input
            data-testid="social-handle-input"
            value={draft.handle}
            onChange={(e) => setDraft({ ...draft, handle: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="yourhandle"
            className="w-full pl-7 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <button data-testid="social-add-button" onClick={add} disabled={saving} className="px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
        </button>
      </div>
    </section>
  );
};
